import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AIContentService } from '../../../services/ai-content.service';

export interface ContentEditRequest {
  tagName: string;
  currentContent: string;
  elementPosition: { x: number; y: number; width: number; height: number };
}

export interface ContentEditResponse {
  success: boolean;
  newContent?: string;
  error?: string;
}

@Component({
  selector: 'app-inline-content-editor',
  imports: [CommonModule, FormsModule],
  templateUrl: './inline-content-editor.component.html',
  styleUrls: ['./inline-content-editor.component.scss']
})
export class InlineContentEditorComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() editRequest: ContentEditRequest | null = null;
  @Output() contentUpdated = new EventEmitter<{ tagName: string; newContent: string }>();
  @Output() editCancelled = new EventEmitter<void>();
  @Output() keepOldContent = new EventEmitter<void>();

  @ViewChild('modalContainer') modalContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('feedbackInput') feedbackInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('contentTextarea') contentTextarea!: ElementRef<HTMLTextAreaElement>;

  // Content state
  originalContent: string = '';
  newContent: string = '';
  userFeedback: string = '';
  isGenerating: boolean = false;

  // Minimized state (for future use)
  isMinimized: boolean = false;
  showAcceptReject: boolean = false;

  constructor(
    private aiContentService: AIContentService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  // Input properties for current data
  @Input() currentSupplierData: any = {};
  @Input() currentSemanticContent: { [tagName: string]: string } = {};

  ngOnInit(): void {
    // Listen for edit requests
    if (this.editRequest) {
      this.showEditor(this.editRequest);
    }
  }

  ngOnChanges(): void {
    console.log('InlineEditor: ngOnChanges called - editRequest:', this.editRequest);
    
    // Handle changes to editRequest input - always process new requests
    if (this.editRequest) {
      console.log('InlineEditor: Edit request received, showing editor');
      this.showEditor(this.editRequest);
    }
  }

  ngAfterViewInit(): void {
    // Focus on content textarea when modal opens
    if (this.contentTextarea) {
      setTimeout(() => {
        this.contentTextarea.nativeElement.focus();
        // Position cursor at the end of the content
        this.contentTextarea.nativeElement.setSelectionRange(
          this.contentTextarea.nativeElement.value.length,
          this.contentTextarea.nativeElement.value.length
        );
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Cleanup
  }

  showEditor(request: ContentEditRequest): void {
    console.log('InlineEditor: Showing editor with request:', request);
    
    if (!request) {
      console.error('InlineEditor: No request provided to showEditor');
      return;
    }
    
    this.editRequest = request;
    this.originalContent = request.currentContent || '';
    this.newContent = request.currentContent || ''; // Start with current content
    this.userFeedback = '';
    this.isGenerating = false;
    this.showAcceptReject = false;

    console.log('InlineEditor: Editor state set - tag:', request.tagName, 'content:', this.newContent);

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();

    // Focus on content textarea after modal is positioned
    setTimeout(() => {
      if (this.contentTextarea) {
        this.contentTextarea.nativeElement.focus();
        // Position cursor at the end of the content
        this.contentTextarea.nativeElement.setSelectionRange(
          this.contentTextarea.nativeElement.value.length,
          this.contentTextarea.nativeElement.value.length
        );
        console.log('InlineEditor: Focused on content textarea');
      }
    }, 100);
  }



  async regenerateContent(): Promise<void> {
    if (!this.editRequest || !this.userFeedback.trim()) {
      return;
    }

    this.isGenerating = true;

    try {
      // Get current supplier data from the service
      const supplierData = this.getCurrentSupplierData();

      // Use the new single tag regeneration method
      const tagReplacements = await this.aiContentService.regenerateSingleTag(
        this.editRequest.tagName,
        this.originalContent,
        this.userFeedback,
        supplierData
      );

      if (tagReplacements.length > 0 && tagReplacements[0].tagName === this.editRequest.tagName) {
        this.newContent = tagReplacements[0].replacementHTML;
        this.showAcceptReject = true;
      } else {
        throw new Error('No content generated for the specified tag');
      }

    } catch (error) {
      console.error('Content regeneration failed:', error);
      alert(`Failed to regenerate content: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.isGenerating = false;
    }
  }

  useNewContent(): void {
    if (this.editRequest && this.newContent.trim()) {
      // Accept: update originalContent to new value, hide buttons, but do NOT close modal
      this.originalContent = this.newContent.trim();
      this.showAcceptReject = false;
    }
  }

  keepOldContentAction(): void {
    // Reject: restore original content and hide buttons
    this.newContent = this.originalContent;
    this.showAcceptReject = false;
    this.keepOldContent.emit();
    // Do not close modal, just revert content and hide buttons
  }

  cancelEdit(): void {
    this.editCancelled.emit();
    this.showAcceptReject = false;
    this.closeModal();
  }

  // This method is called on form submit (Update button)
  onUpdate(): void {
    if (this.editRequest && this.newContent.trim()) {
      this.contentUpdated.emit({
        tagName: this.editRequest.tagName,
        newContent: this.newContent.trim()
      });
      this.closeModal();
    }
  }

  private closeModal(): void {
    this.editRequest = null;
    this.originalContent = '';
    this.newContent = '';
    this.userFeedback = '';
    this.isGenerating = false;
    // Remove highlight from preview
    if (window && window.parent) {
      window.parent.postMessage({ type: 'closeInlineEditor' }, '*');
    } else if (window) {
      window.postMessage({ type: 'closeInlineEditor' }, '*');
    }
  }

  // Helper methods to get current data
  private getCurrentSupplierData(): any {
    return this.currentSupplierData;
  }

  private getCurrentSemanticContent(): { [tagName: string]: string } {
    return this.currentSemanticContent;
  }

  // Handle modal clicks (no backdrop click to close)
  onModalClick(event: Event): void {
    event.stopPropagation();
  }
} 